import { ProDocDocumentDetail, type ProDocDocument } from '@fiveox/prodoc-ui'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { useOrg } from '../components/org-context'
import { useSharingHost } from '../components/sharing-host'
import { SERVICE_API } from '../lib/config'
import { recordVisit } from '../lib/goto'
import { session } from '../lib/session'

/**
 * One document, at its own address — so a deep link opened cold survives
 * sign-in and lands here (spec FR-2). A thin host (ADR-0010): the screen is
 * `<ProDocDocumentDetail>`; the route adds the way back, where archiving
 * leads, the way into Review for a document waiting there, and Go To's Recent
 * — this page is the one place that knows the name.
 */
export function DocumentDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { org, userId } = useOrg()
  const [seen, setSeen] = useState<ProDocDocument | null>(null)
  const sharingHost = useSharingHost()

  useEffect(() => {
    if (!seen) return
    recordVisit(userId, org.id, {
      id: `doc:${seen.id}`,
      kind: 'document',
      label: seen.title,
      to: `/documents/${seen.id}`,
      meta: seen.owner_app !== 'prodoc' ? seen.owner_app : undefined,
      about: 'A document in this organisation.',
    })
  }, [seen, userId, org.id])

  return (
    <ProDocDocumentDetail
      apiUrl={SERVICE_API}
      fetcher={session.authed}
      orgId={org.id}
      userId={userId}
      {...sharingHost}
      documentId={id}
      back={
        <Link to="/documents" className="text-muted-foreground hover:text-foreground">
          ← Documents
        </Link>
      }
      onLoaded={setSeen}
      onChanged={setSeen}
      onArchived={() => navigate('/documents', { replace: true })}
      // Offered only to a reviewer while it waits — the same verb `/review` is routed behind.
      onReview={(doc) => navigate(`/review?doc=${encodeURIComponent(doc.id)}`)}
      // Engine W15: what was read from it, at its own address.
      onOpenExtraction={(doc, extraction) => navigate(`/documents/${encodeURIComponent(doc.id)}/extractions/${encodeURIComponent(extraction.id)}`)}
    />
  )
}
